import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { getApiUrl } from "../../../../api";
import PostForm from "./PostForm";

interface ContentSection { id: number; type?: string; subtitle: string; content?: string; tableData?: any; }
interface DateFields { year: string; month: string; day: string; }
interface FormData {
    title: string; thumbnailImage: File | null; thumbnailImagePreview: string;
    popupContent: string; contentImage: File | null; contentImagePreview: string;
    imageDescription: string; isDecorative: boolean;
    contentSections: ContentSection[]; parentMenu: string;
    startDate: DateFields; endDate: DateFields;
}

const convertBodyToSections = (body: any[]): { sections: ContentSection[]; dividers: Set<number> } => {
    if (!Array.isArray(body)) return { sections: [{ id: 1, subtitle: "", content: "" }], dividers: new Set() };
    const sections: ContentSection[] = [];
    const dividers = new Set<number>();
    let sectionId = 1;
    let pendingDivider = false;
    for (const item of body) {
        if (item.type === "divider") { pendingDivider = true; continue; }
        if (item.type === "text") {
            if (pendingDivider && sections.length > 0) dividers.add(sections.length - 1);
            sections.push({ id: sectionId++, subtitle: item.label || "", content: item.value || "" });
            pendingDivider = false;
        }
        if (item.type === "table") {
            if (pendingDivider && sections.length > 0) dividers.add(sections.length - 1);
            let tableData = item.value;
            if (typeof tableData === "string") { try { tableData = JSON.parse(tableData); } catch { tableData = null; } }
            if (tableData?.tableType) {
                sections.push({ id: sectionId++, type: "table", subtitle: item.label || "", tableData });
            } else {
                const headers = (item.columns || []).map((col: any) => col.label || col.key);
                const keys = (item.columns || []).map((col: any) => col.key);
                const firstRow = Array.isArray(tableData) ? tableData[0] : null;
                const hasOX = firstRow && Object.entries(firstRow).some(([k, v]) => k !== keys[0] && (v === "O" || v === "X"));
                const detectedType = hasOX ? "check" : "text";
                const rows = (Array.isArray(tableData) ? tableData : []).map((rowObj: any) => ({
                    cells: keys.map((key: string, ci: number) => {
                        const raw = rowObj[key] ?? "";
                        const isCheckCell = detectedType === "check" && ci > 0;
                        return { cellType: isCheckCell ? "checkbox" : "text", value: isCheckCell ? (raw === "O") : raw };
                    })
                }));
                sections.push({ id: sectionId++, type: "table", subtitle: item.label || "", tableData: { tableType: detectedType, headers, rows, columnKeys: keys } });
            }
            pendingDivider = false;
        }
    }
    return { sections: sections.length > 0 ? sections : [{ id: 1, subtitle: "", content: "" }], dividers };
};

export default function PostDetail() {
    const navigate = useNavigate();
    const { content_code } = useParams<{ content_code: string }>();
    const [searchParams] = useSearchParams();
    const kioskCode = searchParams.get("kiosk_code") || localStorage.getItem("kiosk_code") || "";
    const categoryCode = searchParams.get("category_code");
    const [dividerSet, setDividerSet] = useState(new Set<number>());
    const [isLoading, setIsLoading] = useState(true);
    const [categoryList, setCategoryList] = useState<{ category_code: string; name: string }[]>([]);
    const [formData, setFormData] = useState<FormData>({
        title: "", thumbnailImage: null, thumbnailImagePreview: "", popupContent: "",
        contentImage: null, contentImagePreview: "", imageDescription: "", isDecorative: false,
        contentSections: [{ id: 1, subtitle: "", content: "" }], parentMenu: "",
        startDate: { year: "", month: "", day: "" }, endDate: { year: "", month: "", day: "" }
    });

    const getCmsToken = () => localStorage.getItem("cms_access_token");

    useEffect(() => {
        const fetchPost = async () => {
            if (!content_code) return;
            try {
                const res = await fetch(getApiUrl(`/api/contents/${content_code}/detail`), { headers: { Authorization: `Bearer ${getCmsToken()}` } });
                if (res.ok) {
                    const { content, parent } = await res.json();
                    if (parent) setCategoryList([{ category_code: parent.category_code, name: parent.name }]);
                    const parseDate = (s: string) => { if (!s) return { year: "", month: "", day: "" }; const d = new Date(s); return { year: d.getFullYear().toString(), month: (d.getMonth()+1).toString().padStart(2,"0"), day: d.getDate().toString().padStart(2,"0") }; };
                    const { sections, dividers } = convertBodyToSections(content.body);
                    setDividerSet(dividers);
                    setFormData({ title: content.title || "", thumbnailImage: null, thumbnailImagePreview: content.thumbnail_url || "", popupContent: content.top_popup_message || "", contentImage: null, contentImagePreview: content.image_url || "", imageDescription: content.image_description || "", isDecorative: content.is_decorative ?? false, contentSections: sections, parentMenu: parent?.category_code || "", startDate: parseDate(content.visible_start_at), endDate: parseDate(content.visible_end_at) });
                }
            } catch (e) { console.error(e); }
            finally { setIsLoading(false); }
        };
        fetchPost();
    }, [content_code]);

    const dummyFn = () => {};
    const dummyFnWithArgs = (_: any) => {};

    if (isLoading) return <div className="bg-[#f5f2f0] min-h-screen flex items-center justify-center"><p className="text-gray-400">불러오는 중...</p></div>;

    return (
        <PostForm
            formData={formData}
            onInputChange={dummyFn}
            onImageSelect={() => () => {}}
            onAddContentSection={dummyFn}
            onRemoveContentSection={dummyFnWithArgs}
            onSectionChange={dummyFn}
            dividerSet={dividerSet}
            onToggleDivider={dummyFnWithArgs}
            onDateChange={dummyFn}
            categoryList={categoryList}
            onSubmit={dummyFn}
            onCancel={() => navigate(-1)}
            mode="view"
            isLoading={false}
            onMoveSection={dummyFn}
            onEditClick={() => navigate(`/admin/kiosk/content/edit/${content_code}?kiosk_code=${kioskCode}&category_code=${categoryCode || formData.parentMenu}`)}
            onAddTable={dummyFnWithArgs}
            onTableHeaderChange={dummyFn}
            onTableCellChange={dummyFn}
            onTableAddRow={dummyFnWithArgs}
            onTableRemoveRow={dummyFn}
            onTableAddCol={dummyFnWithArgs}
            onTableRemoveCol={dummyFnWithArgs}
            onSectionsReorder={dummyFnWithArgs}
            onOpenImageModal={dummyFnWithArgs}
            onRemoveImage={dummyFn}
        />
    );
}